import Link from 'next/link';
import * as React from 'react';
import { useIntl } from 'react-intl';
import BooksNotes from '../../../../molecules/merchant/resources/book/BooksNotes';
import { BookingPageScheme } from '../../../../domain/entities/BookingPage';
import { CategoryEntity } from '../../../../interfaces/explore/CategoryEntity';
import {
  MerchantBookingPageApiResponse,
  MerchantBookingPhotoGalleryImage,
  MerchantBookingStatus,
} from '../../../../interfaces/merchants/bookings';
import { MerchantBookingCourse } from '../../../../interfaces/merchants/bookings/courses';
import { MerchantBookingStaffApi } from '../../../../interfaces/merchants/bookings/staff';
import * as IBooks from '../../../../interfaces/merchants/resources/books';

import { MerchantReview } from '../../../../interfaces/merchants/reviews';
import Button, { ButtonSize } from '../../../../atoms/buttons/Button';
import Lightbox, { LightboxImage } from '../../../../components/Lightbox';
import GoogleMap from '../../../../molecules/common/GoogleMap';
import ReviewList from '../../../../molecules/merchant/reviews/ReviewList';
import Heading from '../../../../atoms/Heading';
import BaseTemplate from '../../../../organisms/merchants/BaseTemplate';
import DescriptionPanel from '../../../../molecules/merchant/DescriptionPanel';
import { useResourceNotes } from '../../../../helpers/merchants/resources/helper';
import MerchantProfile from '../../../../molecules/merchant/MerchantProfile';
import MerchantCourseDetailsAppointment from './components/Appointment';

import css from './MerchantBookingServiceShowPage.module.scss';
import MerchantCourseDetailsHeader from './components/MerchantCourseDetailsHeader';
import MerchantCourseDetailsHeaderImage from './components/MerchantCourseDetailsHeaderImage';
import MerchantCourseDetailsReservationButton from '../../../../molecules/merchant/resources/book/MerchantCourseDetailsReservationButton';

import { MerchantDetail } from '../../../../interfaces/merchants';
import MerchantCourseDetailsStaffList from '../../../../molecules/merchant/resources/book/MerchantCourseDetailsStaffList';

const MAX_REVIEWS = 3;

export const mapPhotoGalleryImageToLightbox = (
  images: MerchantBookingPhotoGalleryImage[],
): LightboxImage[] => {
  const lightboxImages = images
    .map((image, i) => ({
      thumbUrl: findThumb(image),
      mainUrl: findImage(image),
      id: i,
    }))
    .filter((image) => image.mainUrl !== '' && image.thumbUrl !== '');

  return lightboxImages;
};

export interface PageProps {
  bookingData: MerchantBookingPageApiResponse;
  isBookable: boolean;
  reviews: MerchantReview[];
  settings: IBooks.SettingCompletion;
  merchantDetail: MerchantDetail;
  categories: CategoryEntity[];
  courses: MerchantBookingCourse[];
  staff: MerchantBookingStaffApi[];
  isCoubicApp: boolean;
}

const MerchantbookingPageShowPage = ({
  bookingData,
  isBookable,
  settings,
  merchantDetail,
  categories,
  courses,
  staff,
  reviews,
  isCoubicApp,
}: PageProps) => {
  const intl = useIntl();
  const canShowReviews =
    merchantDetail.merchant_setting.is_customer_review_active &&
    merchantDetail.rate_count_starby &&
    reviews &&
    reviews.length > 0;

  const {
    cover_content,
    cover_contents_type,
    description,
    description_text,
    primary_image,
    is_json_description,
    ...headerProps
  } = bookingData;
  const notes = useResourceNotes(settings);

  const isEvent = bookingData.model_type === BookingPageScheme.Event;
  const isFullyBooked =
    bookingData.status === MerchantBookingStatus.FullyBooked;
  // explore掲載専用マーチャントの場合、予約に関しての注意事項が紛らわしいので見せない。(予約導線はクービック内に無い為)
  const attentionForBookingVisible =
    !merchantDetail.merchant_setting.is_explore_only;

  return (
    <BaseTemplate
      footerProps={{ categories }}
      merchantDetail={merchantDetail}
      bookingPageIsBookable={isBookable}
      isCoubicApp={isCoubicApp}
    >
      <div className={css.MerchantBookingServiceShowPage}>
        <div className={css.MerchantBookingServiceShowPage__Heading}>
          <MerchantCourseDetailsHeader
            isBookable={isBookable}
            merchantSetting={merchantDetail.merchant_setting}
            isEvent={isEvent}
            isFullyBooked={isFullyBooked}
            {...headerProps}
          />
        </div>
        <div className={css.MerchantBookingServiceShowPage__HeroImage}>
          {cover_contents_type === 'upload_image' && primary_image && (
            <MerchantCourseDetailsHeaderImage
              name={headerProps.name}
              primary_image={primary_image}
            />
          )}
          {cover_contents_type === 'embed' &&
            cover_content &&
            cover_content.embed && (
              <div
                className={css.MerchantBookingServiceShowPage__Embed}
                dangerouslySetInnerHTML={{ __html: cover_content.embed }}
              />
            )}
        </div>
        {((is_json_description && description) || description_text) && (
          <div className={css.MerchantBookingServiceShowPage__Section}>
            <DescriptionPanel
              description={description}
              description_text={description_text}
              is_json_description={is_json_description}
            />
          </div>
        )}
        {isBookable && courses.length > 0 && (
          <div className={css.MerchantBookingServiceShowPage__Section}>
            <Heading className={css.MerchantBookingServiceShowPage__Heading}>
              {intl.formatMessage({
                id: 'books.formsLabel.appointment',
              })}
            </Heading>
            <MerchantCourseDetailsAppointment
              nickname={merchantDetail.public_id}
              resourceId={bookingData.public_id}
              courses={courses}
              staff={staff}
            />
          </div>
        )}
        {isEvent && staff.length > 0 && (
          <div className={css.MerchantBookingServiceShowPage__Section}>
            <Heading className={css.MerchantBookingServiceShowPage__Heading}>
              {intl.formatMessage({
                id: 'books.formsLabel.staff',
              })}
            </Heading>
            <MerchantCourseDetailsStaffList staff={staff} />
          </div>
        )}
        {attentionForBookingVisible && (
          <div className={css.MerchantBookingServiceShowPage__Section}>
            <Heading className={css.MerchantBookingServiceShowPage__Heading}>
              {intl.formatMessage({
                id: 'books.formsLabel.attentionForBooking',
              })}
            </Heading>
            <BooksNotes notes={notes} />
            <p className={css.MerchantBookingServiceShowPage__ButtonContainer}>
              <MerchantCourseDetailsReservationButton
                isBookable={isBookable}
                contactUrl={merchantDetail.merchant_setting.contact_url}
                {...bookingData}
              />
            </p>
          </div>
        )}
        {bookingData.map_widget && (
          <div className={css.MerchantBookingServiceShowPage__Section}>
            <Heading className={css.MerchantBookingServiceShowPage__Heading}>
              {intl.formatMessage({
                id: 'merchants.details.access',
              })}
            </Heading>
            <GoogleMap
              displayName={merchantDetail.display_name}
              mapData={bookingData.map_widget}
            />
          </div>
        )}
        {bookingData.photo_gallery_images.length >= 1 && (
          <div className={css.MerchantBookingServiceShowPage__Section}>
            <Heading className={css.MerchantBookingServiceShowPage__Heading}>
              {intl.formatMessage({
                id: 'merchants.bookingPages.photoGallery',
              })}
            </Heading>
            <Lightbox
              images={mapPhotoGalleryImageToLightbox(
                bookingData.photo_gallery_images,
              )}
              alt={bookingData.name}
            />
          </div>
        )}
        {canShowReviews && (
          <div className={css.MerchantBookingServiceShowPage__Section}>
            <Heading className={css.MerchantBookingServiceShowPage__Heading}>
              {intl.formatMessage({ id: 'reviews.title' })}
            </Heading>
            <ReviewList
              reviews={reviews}
              merchantDetail={merchantDetail}
              max={MAX_REVIEWS}
              rate_count_starby={merchantDetail.rate_count_starby}
              showHeader={true}
            />
            {reviews.length > 0 && (
              <Link
                href={`/${merchantDetail.public_id}/reviews`}
                prefetch={false}
                className={css.MerchantBookingServiceShowPage__ReviewLink}
              >
                {intl.formatMessage({
                  id: 'merchants.details.showMoreReviews',
                })}
              </Link>
            )}
          </div>
        )}
        <div className={css.MerchantBookingServiceShowPage__Section}>
          <Heading className={css.MerchantBookingServiceShowPage__Heading}>
            {intl.formatMessage({ id: 'merchants.bookingPages.storeData' })}
          </Heading>
          <MerchantProfile merchantDetail={merchantDetail} />
          <div
            className={
              css.MerchantBookingServiceShowPage__BackToTopButtonSection
            }
          >
            <Button
              className_Deprecated={
                css.MerchantBookingServiceShowPage__BackToTopButton
              }
              isGhost={true}
              isFull={true}
              href={`/${merchantDetail.public_id}`}
              buttonSize={ButtonSize.Medium}
            >
              {intl.formatMessage(
                {
                  id: 'merchants.details.backToMerchantTop',
                },
                { displayName: merchantDetail.display_name },
              )}
            </Button>
          </div>
        </div>
      </div>
    </BaseTemplate>
  );
};

//  findThumb is helper
function findThumb(data: MerchantBookingPhotoGalleryImage): string {
  if (data.image.thumb && data.image.thumb.url) {
    return data.image.thumb.url || '';
  }
  if (data.image.square && data.image.square.url) {
    return data.image.square.url || '';
  }
  if (data.image.p800x600 && data.image.p800x600.url) {
    return data.image.p800x600.url || '';
  }
  if (data.image.p1280x1280 && data.image.p1280x1280.url) {
    return data.image.p1280x1280.url || '';
  }
  if (data.image && data.image.url) {
    return data.image.url || '';
  }
  return '';
}

//  findImage is helper
function findImage(image: MerchantBookingPhotoGalleryImage): string {
  if (image.image.p1280x1280 && image.image.p1280x1280.url) {
    return image.image.p1280x1280.url || '';
  }
  if (image.image.p800x600 && image.image.p800x600.url) {
    return image.image.p800x600.url || '';
  }
  if (image.image && image.image.url) {
    return image.image.url || '';
  }
  if (image.image.square && image.image.square.url) {
    return image.image.square.url || '';
  }
  if (image.image.thumb && image.image.thumb.url) {
    return image.image.thumb.url || '';
  }
  return '';
}

export default MerchantbookingPageShowPage;
